import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { tournamentsAPI, statsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const LEVELS = ['olympic','world','world_cup','grand_prix','continental','arab','gulf','national','local'];
const LEVEL_LABELS = { olympic:'🏅 الألعاب الأولمبية', world:'🌍 بطولة العالم', world_cup:'🏆 كأس العالم', grand_prix:'🎖️ جائزة كبرى', continental:'🌐 بطولة قارية', arab:'🌙 بطولة عربية', gulf:'⛽ بطولة خليجية', national:'🏳️ بطولة وطنية', local:'📍 بطولة محلية' };

const EMPTY_FORM = { name:'', name_ar:'', sport_id:'', level:'national', start_date:'', end_date:'', location:'', organizer:'', description:'' };

function TournamentModal({ tournament, sports, onClose, onSave }) {
  const [form, setForm] = useState(tournament || EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const handleSubmit = async () => { setLoading(true); try { await onSave(form); onClose(); } catch(e) { toast.error(e.response?.data?.message||'حدث خطأ'); } finally { setLoading(false); } };
  const inp = { background:'var(--bg-secondary)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', color:'var(--text-primary)', fontFamily:'inherit', width:'100%', boxSizing:'border-box' };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-header"><h3>{tournament?'تعديل البطولة':'إضافة بطولة جديدة'}</h3><button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group"><label className="form-label">اسم البطولة (AR)</label><input style={inp} value={form.name_ar||''} onChange={e=>set('name_ar',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Tournament Name (EN)</label><input style={inp} value={form.name||''} onChange={e=>set('name',e.target.value)}/></div>
          </div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">الرياضة</label>
              <select style={inp} value={form.sport_id||''} onChange={e=>set('sport_id',e.target.value)}>
                <option value="">اختر الرياضة</option>
                {sports?.data?.map(s=><option key={s.id} value={s.id}>{s.icon} {s.name_ar||s.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">مستوى البطولة</label>
              <select style={inp} value={form.level||'national'} onChange={e=>set('level',e.target.value)}>
                {LEVELS.map(l=><option key={l} value={l}>{LEVEL_LABELS[l]}</option>)}
              </select>
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">تاريخ البداية</label><input style={inp} type="date" value={form.start_date||''} onChange={e=>set('start_date',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">تاريخ النهاية</label><input style={inp} type="date" value={form.end_date||''} onChange={e=>set('end_date',e.target.value)}/></div>
          </div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">الموقع</label><input style={inp} value={form.location||''} onChange={e=>set('location',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">الجهة المنظمة</label><input style={inp} value={form.organizer||''} onChange={e=>set('organizer',e.target.value)}/></div>
          </div>
          <div className="form-group"><label className="form-label">وصف البطولة</label><textarea style={{...inp,height:80}} value={form.description||''} onChange={e=>set('description',e.target.value)}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>إلغاء</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>{loading?'جاري الحفظ...':'💾 حفظ'}</button>
        </div>
      </div>
    </div>
  );
}

export default function Tournaments() {
  const navigate = useNavigate();
  const { isAdmin, isEditor } = useAuth();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null);
  const [filters, setFilters] = useState({ level:'', sport_id:'', page:1 });

  const { data, isLoading } = useQuery({ queryKey:['tournaments',filters], queryFn:()=>tournamentsAPI.getAll(filters).then(r=>r.data) });
  const { data: sports } = useQuery({ queryKey:['sports'], queryFn:()=>statsAPI.sports().then(r=>r.data) });

  const createMut = useMutation({ mutationFn:tournamentsAPI.create, onSuccess:()=>{ toast.success('تمت إضافة البطولة'); queryClient.invalidateQueries(['tournaments']); } });
  const updateMut = useMutation({ mutationFn:({id,d})=>tournamentsAPI.update(id,d), onSuccess:()=>{ toast.success('تم التحديث'); queryClient.invalidateQueries(['tournaments']); } });
  const deleteMut = useMutation({ mutationFn:tournamentsAPI.delete, onSuccess:()=>{ toast.success('تم الحذف'); queryClient.invalidateQueries(['tournaments']); } });

  return (
    <div>
      <div className="topbar">
        <span className="topbar-title">🏆 البطولات</span>
        {(isAdmin||isEditor) && <button className="btn btn-primary btn-sm" onClick={()=>setModal({mode:'add'})}>+ إضافة بطولة</button>}
      </div>
      <div className="page-content">
        <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap'}}>
          <select style={{background:'var(--bg-secondary)',border:'1px solid var(--border)',borderRadius:8,padding:'8px 12px',color:'var(--text-primary)',fontFamily:'inherit'}} onChange={e=>setFilters(f=>({...f,level:e.target.value,page:1}))}>
            <option value="">جميع المستويات</option>
            {LEVELS.map(l=><option key={l} value={l}>{LEVEL_LABELS[l]}</option>)}
          </select>
          <select style={{background:'var(--bg-secondary)',border:'1px solid var(--border)',borderRadius:8,padding:'8px 12px',color:'var(--text-primary)',fontFamily:'inherit'}} onChange={e=>setFilters(f=>({...f,sport_id:e.target.value,page:1}))}>
            <option value="">جميع الرياضات</option>
            {sports?.data?.map(s=><option key={s.id} value={s.id}>{s.icon} {s.name_ar||s.name}</option>)}
          </select>
        </div>

        {isLoading ? <div className="loading"><div className="spinner"/></div> : (
          <div style={{display:'grid',gap:12}}>
            {data?.data?.map(t=>(
              <div key={t.id} className="card" style={{cursor:'pointer'}} onClick={()=>navigate(`/tournaments/${t.id}`)}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:16,marginBottom:4}}>{t.name_ar||t.name}</div>
                    <div style={{fontSize:13,color:'var(--text-secondary)',display:'flex',gap:12,flexWrap:'wrap'}}>
                      {t.sport_icon && <span>{t.sport_icon} {t.sport_name_ar||t.sport_name}</span>}
                      {t.start_date && <span>📅 {t.start_date?.slice(0,10)}</span>}
                      {t.location && <span>📍 {t.location}</span>}
                      {t.organizer && <span>🏢 {t.organizer}</span>}
                    </div>
                  </div>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <span className={`level-badge level-${t.level}`}>{LEVEL_LABELS[t.level]||t.level}</span>
                    {(isAdmin||isEditor) && <>
                      <button style={{background:'rgba(201,168,76,0.2)',border:'none',borderRadius:6,padding:'4px 10px',color:'var(--gold)',cursor:'pointer'}} onClick={e=>{e.stopPropagation();setModal({mode:'edit',data:t});}}>✎ تعديل</button>
                      {isAdmin && <button style={{background:'rgba(239,68,68,0.2)',border:'none',borderRadius:6,padding:'4px 10px',color:'#fca5a5',cursor:'pointer'}} onClick={e=>{e.stopPropagation();if(window.confirm('حذف البطولة؟'))deleteMut.mutate(t.id);}}>✕ حذف</button>}
                    </>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && <TournamentModal
        tournament={modal.mode==='edit'?modal.data:null}
        sports={sports}
        onClose={()=>setModal(null)}
        onSave={async(form)=>{ if(modal.mode==='add') await createMut.mutateAsync(form); else await updateMut.mutateAsync({id:modal.data.id,d:form}); }}
      />}
    </div>
  );
}
